import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiService from '../../api/ApiService';
import AdminSectionNav from '../../components/AdminSectionNav/AdminSectionNav';
import { useAuth } from '../../components/auth/AuthContext';
import './AdminPointsCampaignsPage.css';

const PAGE_SIZE = 20;
const CLAIMS_PAGE_SIZE = 50;
const CHANNEL_CODE = 'juchacha_canvas';

const STATUS_LABELS = {
  draft: '草稿',
  upcoming: '未开始',
  active: '进行中',
  full: '已满员',
  paused: '已暂停',
  ended: '已结束'
};

const beijingInputValue = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(date).reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

const addDaysInput = (days) => beijingInputValue(Date.now() + days * 24 * 60 * 60 * 1000);
const toApiTime = (value) => value ? `${value}:00+08:00` : '';

const emptyForm = () => ({
  campaign_code: '',
  channel_code: CHANNEL_CODE,
  title: '',
  subtitle: '报名领取积分，开启你的灵感创作',
  rules: '每位用户本期活动限领取一次，名额有限，领完即止。',
  reward_points: '500',
  participant_limit: '1000',
  start_at: beijingInputValue(),
  end_at: addDaysInput(7),
  cta_text: '立即报名领取',
  cover_url: '',
  sort_order: '0'
});

const campaignToForm = (campaign) => ({
  campaign_code: campaign.campaign_code,
  channel_code: campaign.channel_code,
  title: campaign.title,
  subtitle: campaign.subtitle || '',
  rules: campaign.rules || '',
  reward_points: String(campaign.reward_points),
  participant_limit: String(campaign.participant_limit),
  start_at: beijingInputValue(campaign.start_at),
  end_at: beijingInputValue(campaign.end_at),
  cta_text: campaign.cta_text || '立即报名领取',
  cover_url: campaign.cover_url || '',
  sort_order: String(campaign.sort_order || 0)
});

const formatTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('zh-CN', { hour12: false, timeZone: 'Asia/Shanghai' });
};

const extractErrorMessage = (error, fallback) => {
  let data = error?.data;
  if (typeof data === 'string') {
    try { data = JSON.parse(data); } catch (_) {}
  }
  const detail = data?.detail || data?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return error?.msg || error?.message || fallback;
};

function AdminPointsCampaignsPage() {
  const { user } = useAuth();
  const apiService = useMemo(() => new ApiService(), []);
  const [campaigns, setCampaigns] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [formDialog, setFormDialog] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [claimsDialog, setClaimsDialog] = useState(null);
  const [claimsLoading, setClaimsLoading] = useState(false);

  const loadCampaigns = useCallback(async () => {
    if (!user?.token) return;
    setLoading(true);
    setError('');
    try {
      const response = await apiService.adminListPointsCampaigns(user.token, {
        channel_code: CHANNEL_CODE,
        page,
        page_size: PAGE_SIZE
      });
      const data = response?.data || {};
      setCampaigns(Array.isArray(data.list) ? data.list : []);
      setTotalPages(Number(data.total_pages) || 0);
    } catch (loadError) {
      setError(extractErrorMessage(loadError, '活动列表加载失败'));
    } finally {
      setLoading(false);
    }
  }, [apiService, page, user?.token]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const openCreate = (source = null) => {
    if (source) {
      const copied = campaignToForm(source);
      copied.campaign_code = `${source.campaign_code}_next`.slice(0, 64);
      copied.title = `${source.title}（新一期）`;
      copied.start_at = beijingInputValue(source.end_at);
      copied.end_at = beijingInputValue(new Date(source.end_at).getTime() + 7 * 24 * 60 * 60 * 1000);
      setForm(copied);
    } else {
      setForm(emptyForm());
    }
    setFormDialog({ mode: 'create', campaign: null });
  };

  const openEdit = (campaign) => {
    setForm(campaignToForm(campaign));
    setFormDialog({ mode: 'edit', campaign });
  };

  const updateForm = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submitForm = async (event) => {
    event.preventDefault();
    const editing = formDialog?.mode === 'edit';
    const campaignId = formDialog?.campaign?.id;
    const payload = {
      title: form.title.trim(),
      subtitle: form.subtitle.trim(),
      rules: form.rules.trim(),
      reward_points: Number(form.reward_points),
      participant_limit: Number(form.participant_limit),
      start_at: toApiTime(form.start_at),
      end_at: toApiTime(form.end_at),
      cta_text: form.cta_text.trim(),
      cover_url: form.cover_url.trim(),
      sort_order: Number(form.sort_order) || 0
    };
    if (!editing) {
      payload.campaign_code = form.campaign_code.trim().toLowerCase();
      payload.channel_code = CHANNEL_CODE;
    }

    setSavingId(editing ? campaignId : 'create');
    try {
      if (editing) {
        await apiService.adminUpdatePointsCampaign(user.token, campaignId, payload);
        toast.success('活动配置已保存');
      } else {
        await apiService.adminCreatePointsCampaign(user.token, payload);
        toast.success('活动草稿已创建');
      }
      setFormDialog(null);
      await loadCampaigns();
    } catch (saveError) {
      toast.error(extractErrorMessage(saveError, '活动保存失败'));
    } finally {
      setSavingId(null);
    }
  };

  const changeStatus = async (campaign, action) => {
    if (action === 'end' && !window.confirm(`确认提前结束“${campaign.title}”吗？`)) return;
    setSavingId(campaign.id);
    try {
      await apiService.adminChangePointsCampaignStatus(user.token, campaign.id, action);
      const messages = { publish: '活动已发布', pause: '活动已暂停', resume: '活动已恢复', end: '活动已结束' };
      toast.success(messages[action] || '活动状态已更新');
      await loadCampaigns();
    } catch (statusError) {
      toast.error(extractErrorMessage(statusError, '活动状态更新失败'));
    } finally {
      setSavingId(null);
    }
  };

  const loadClaims = async (campaign, claimsPage) => {
    setClaimsLoading(true);
    try {
      const response = await apiService.adminListPointsCampaignClaims(
        user.token,
        campaign.id,
        claimsPage,
        CLAIMS_PAGE_SIZE
      );
      const data = response?.data || {};
      setClaimsDialog({
        campaign,
        list: data.list || [],
        total: Number(data.total) || 0,
        page: Number(data.page) || claimsPage,
        totalPages: Number(data.total_pages) || 0,
        error: ''
      });
    } catch (claimError) {
      setClaimsDialog({
        campaign,
        list: [],
        total: 0,
        page: claimsPage,
        totalPages: 0,
        error: extractErrorMessage(claimError, '领取名单加载失败')
      });
    } finally {
      setClaimsLoading(false);
    }
  };

  const openClaims = (campaign) => {
    setClaimsDialog({ campaign, list: [], total: 0, page: 1, totalPages: 0, error: '' });
    loadClaims(campaign, 1);
  };

  return (
    <main className="admin-points-campaigns-page">
      <header className="admin-points-campaigns-header">
        <div>
          <span>CAMPAIGN ADMIN</span>
          <h1>积分活动管理</h1>
          <p>配置活动期数、北京时间、领取人数与积分奖励。</p>
        </div>
        <div>
          <button type="button" onClick={() => openCreate()}>新建活动</button>
          <Link to="/">返回灵感画布</Link>
        </div>
      </header>

      <AdminSectionNav />

      <section className="admin-points-campaigns-panel" aria-label="积分活动列表">
        {loading && <div className="admin-points-campaigns-message" role="status">正在读取活动列表…</div>}
        {error && (
          <div className="admin-points-campaigns-message is-error" role="alert">
            <span>{error}</span><button type="button" onClick={loadCampaigns}>重试</button>
          </div>
        )}
        {!loading && !error && campaigns.length === 0 && (
          <div className="admin-points-campaigns-message">还没有活动，点击“新建活动”创建第一期。</div>
        )}
        {!loading && campaigns.length > 0 && (
          <div className="admin-points-campaigns-table-wrap">
            <table className="admin-points-campaigns-table">
              <thead>
                <tr>
                  <th>活动</th><th>状态</th><th>奖励</th><th>领取进度</th><th>活动时间（北京时间）</th><th>操作</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign) => {
                  const saving = savingId === campaign.id;
                  const statusName = campaign.computed_status || campaign.status;
                  return (
                    <tr key={campaign.id}>
                      <td>
                        <strong>{campaign.title}</strong>
                        <small>{campaign.campaign_code}</small>
                      </td>
                      <td><span className={`admin-points-campaigns-status is-${statusName}`}>{STATUS_LABELS[statusName] || statusName}</span></td>
                      <td><strong>{campaign.reward_points} 积分</strong></td>
                      <td>
                        <strong>{campaign.claimed_count} / {campaign.participant_limit}</strong>
                        <small>剩余 {campaign.remaining_count} 名</small>
                      </td>
                      <td>
                        <time dateTime={campaign.start_at}>{formatTime(campaign.start_at)}</time>
                        <small>至 {formatTime(campaign.end_at)}</small>
                      </td>
                      <td>
                        <div className="admin-points-campaigns-actions">
                          <button type="button" disabled={saving} onClick={() => openEdit(campaign)}>编辑</button>
                          {campaign.status === 'draft' && <button type="button" disabled={saving} onClick={() => changeStatus(campaign, 'publish')}>发布</button>}
                          {campaign.status === 'published' && <button type="button" disabled={saving} onClick={() => changeStatus(campaign, 'pause')}>暂停</button>}
                          {campaign.status === 'paused' && <button type="button" disabled={saving} onClick={() => changeStatus(campaign, 'resume')}>恢复</button>}
                          {campaign.status !== 'ended' && campaign.status !== 'draft' && (
                            <button className="is-danger" type="button" disabled={saving} onClick={() => changeStatus(campaign, 'end')}>结束</button>
                          )}
                          <button type="button" disabled={saving} onClick={() => openClaims(campaign)}>领取名单</button>
                          <button type="button" disabled={saving} onClick={() => openCreate(campaign)}>复制新一期</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <nav className="admin-points-campaigns-pagination" aria-label="活动列表分页">
            <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}>上一页</button>
            <span>第 {page} / {totalPages} 页</span>
            <button type="button" disabled={page >= totalPages || loading} onClick={() => setPage((value) => value + 1)}>下一页</button>
          </nav>
        )}
      </section>

      {formDialog && (
        <div className="admin-points-campaigns-dialog-backdrop" onMouseDown={() => setFormDialog(null)}>
          <section className="admin-points-campaigns-dialog" role="dialog" aria-modal="true" aria-label={formDialog.mode === 'create' ? '新建积分活动' : '编辑积分活动'} onMouseDown={(event) => event.stopPropagation()}>
            <header><div><span>CAMPAIGN SETTINGS</span><h2>{formDialog.mode === 'create' ? '新建积分活动' : '编辑积分活动'}</h2></div><button type="button" aria-label="关闭" onClick={() => setFormDialog(null)}>×</button></header>
            <form onSubmit={submitForm}>
              <div className="admin-points-campaigns-form-grid">
                <label><span>活动编码 *</span><input value={form.campaign_code} disabled={formDialog.mode === 'edit'} required pattern="[a-z0-9][a-z0-9_-]{2,63}" onChange={(event) => updateForm('campaign_code', event.target.value)} /></label>
                <label><span>活动标题 *</span><input value={form.title} required maxLength="100" onChange={(event) => updateForm('title', event.target.value)} /></label>
                <label className="is-wide"><span>首页副标题</span><input value={form.subtitle} maxLength="255" onChange={(event) => updateForm('subtitle', event.target.value)} /></label>
                <label><span>奖励积分 *</span><input type="number" min="0.0001" step="0.0001" value={form.reward_points} required onChange={(event) => updateForm('reward_points', event.target.value)} /></label>
                <label><span>人数上限 *</span><input type="number" min="1" step="1" value={form.participant_limit} required onChange={(event) => updateForm('participant_limit', event.target.value)} /></label>
                <label><span>开始时间（北京时间）*</span><input type="datetime-local" value={form.start_at} required onChange={(event) => updateForm('start_at', event.target.value)} /></label>
                <label><span>结束时间（北京时间）*</span><input type="datetime-local" value={form.end_at} required onChange={(event) => updateForm('end_at', event.target.value)} /></label>
                <label><span>按钮文案</span><input value={form.cta_text} maxLength="64" onChange={(event) => updateForm('cta_text', event.target.value)} /></label>
                <label><span>排序权重</span><input type="number" step="1" value={form.sort_order} onChange={(event) => updateForm('sort_order', event.target.value)} /></label>
                <label className="is-wide"><span>封面图片 URL</span><input type="url" value={form.cover_url} maxLength="500" onChange={(event) => updateForm('cover_url', event.target.value)} /></label>
                <label className="is-wide"><span>活动规则</span><textarea rows="4" value={form.rules} onChange={(event) => updateForm('rules', event.target.value)} /></label>
              </div>
              <footer><button type="button" onClick={() => setFormDialog(null)}>取消</button><button className="is-primary" type="submit" disabled={savingId !== null}>{savingId !== null ? '保存中…' : '保存活动'}</button></footer>
            </form>
          </section>
        </div>
      )}

      {claimsDialog && (
        <div className="admin-points-campaigns-dialog-backdrop" onMouseDown={() => setClaimsDialog(null)}>
          <section className="admin-points-campaigns-dialog is-claims" role="dialog" aria-modal="true" aria-label="活动领取名单" onMouseDown={(event) => event.stopPropagation()}>
            <header><div><span>CLAIM RECORDS</span><h2>{claimsDialog.campaign.title}</h2><p>共 {claimsDialog.total} 人领取</p></div><button type="button" aria-label="关闭" onClick={() => setClaimsDialog(null)}>×</button></header>
            {claimsLoading && <div className="admin-points-campaigns-message">正在读取领取名单…</div>}
            {claimsDialog.error && <div className="admin-points-campaigns-message is-error">{claimsDialog.error}</div>}
            {!claimsLoading && !claimsDialog.error && claimsDialog.list.length === 0 && <div className="admin-points-campaigns-message">本期还没有用户领取</div>}
            {!claimsLoading && claimsDialog.list.length > 0 && (
              <div className="admin-points-campaigns-claims-wrap"><table><thead><tr><th>用户</th><th>积分</th><th>余额变化</th><th>领取时间</th><th>流水号</th></tr></thead><tbody>{claimsDialog.list.map((claim) => <tr key={claim.id}><td><strong>{claim.user_name || `用户 ${claim.user_id}`}</strong><small>ID {claim.user_id}</small></td><td>{claim.reward_points}</td><td>{claim.balance_before} → {claim.balance_after}</td><td>{formatTime(claim.claimed_at)}</td><td><small title={claim.order_id}>{claim.order_id}</small></td></tr>)}</tbody></table></div>
            )}
            {claimsDialog.totalPages > 1 && (
              <nav className="admin-points-campaigns-pagination" aria-label="领取名单分页">
                <button type="button" disabled={claimsDialog.page <= 1 || claimsLoading} onClick={() => loadClaims(claimsDialog.campaign, claimsDialog.page - 1)}>上一页</button>
                <span>第 {claimsDialog.page} / {claimsDialog.totalPages} 页</span>
                <button type="button" disabled={claimsDialog.page >= claimsDialog.totalPages || claimsLoading} onClick={() => loadClaims(claimsDialog.campaign, claimsDialog.page + 1)}>下一页</button>
              </nav>
            )}
          </section>
        </div>
      )}
    </main>
  );
}

export default AdminPointsCampaignsPage;
